import { fhirBaseUrl, openmrsFetch, restBaseUrl } from '@openmrs/esm-framework';
import type { OpenmrsEncounter } from '../types';
import useSWR from 'swr';
import { encounterRepresentation } from '../constants';
import useSWRImmutable from 'swr';
import { useEffect, useState } from 'react';
type UseEncounters = {
  encounters: Array<OpenmrsEncounter>;
  isError: Error | null;
  isLoading: boolean;
  isValidating: boolean;
  mutate: () => void;
};

export function useEncounters(patientUuid: string, encounterType): UseEncounters {
  const encountersUrl = `${restBaseUrl}/encounter?patient=${patientUuid}&encounterType=${encounterType}&v=${encounterRepresentation}`;

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    { data: { results: Array<OpenmrsEncounter> } },
    Error
  >(patientUuid ? encountersUrl : null, openmrsFetch);

  return {
    encounters: data ? data?.data.results : [],
    isError: error,
    isLoading,
    isValidating,
    mutate,
  };
}

// export function useEncounterRows(patientUuid: string, encounterType: string, pageSize?: number, pageNumber?: number) {
//   const [encounters, setEncounters] = useState([]);
//   const startIndex = (pageNumber - 1) * pageSize;

//   const url = `/ws/rest/v1/encounter?encounterType=${encounterType}&patient=${patientUuid}&v=${encounterRepresentation}&totalCount=true&limit=${pageSize}&startIndex=${startIndex}`;

//   const { data: response, error, isLoading, mutate } = useSWR<{ data: any }, Error>(url, openmrsFetch);

//   useEffect(() => {
//     if (response) {
//       response.data.results.sort(
//         (a, b) => new Date(b.encounterDatetime).getTime() - new Date(a.encounterDatetime).getTime(),
//       );

//       setEncounters([...response.data.results]);
//     }
//   }, [response]);

//   return {
//     encounters,
//     total: response?.data?.totalCount,
//     isLoading,
//     isError: error,
//     mutate,
//   };
// }

export function useEncounterRows(patientUuid: string, encounterType: string, pageSize?: number, pageNumber?: number) {
  const [encounters, setEncounters] = useState([]);
  const startIndex = (pageNumber - 1) * pageSize;

  const url = `/ws/rest/v1/encounter?encounterType=${encounterType}&patient=${patientUuid}&v=${encounterRepresentation}&totalCount=true&limit=${pageSize}&startIndex=${startIndex}&order=desc`;


  const { data: response, error, isLoading, mutate } = useSWR<{ data: any }, Error>(url, openmrsFetch);

  useEffect(() => {
    if (response) {
      // Step 1: Sort ascending (oldest → latest)
      const sortedEncounters = [...response.data.results].sort(
        (a, b) => new Date(a.encounterDatetime).getTime() - new Date(b.encounterDatetime).getTime()
      );

      // Step 2: Reverse order to display latest encounter at top
      setEncounters(sortedEncounters.reverse());
    }
  }, [response]);

    return {
    encounters,
    total: response?.data?.totalCount,
    isLoading,
    isError: error,
    mutate,
  };
}


export function usePatientDeathStatus(patientUuid: string) {
  const {
    data: response,
    isLoading,
    error,
  } = useSWRImmutable<any, Error>(`/ws/rest/v1/person/${patientUuid}?v=custom:(dead)`, openmrsFetch);

  return {
    isDead: !isLoading && !error && response ? response?.data?.dead : false,
  };
}

export function useLatestObs(patientUuid: string, conceptUuid: string, encounterType?: string) {
  const searchParams = new URLSearchParams({
    patient: patientUuid,
    code: conceptUuid,
    _sort: '-_lastUpdated',
    _count: '1',
  });
  if (encounterType) {
    searchParams.append('encounter.type', encounterType);
  }

  const url = `${fhirBaseUrl}/Observation?${searchParams.toString()}`;

  const { data: response, error, isLoading } = useSWR<{ data: any }, Error>(url, openmrsFetch);

  return {
    latestMatched: response?.data?.entry?.length ? response?.data?.entry[0]?.resource : null,
    latestLoading: isLoading,
    latestError: error,
  };
}

export function useFetchLocation() {
  const [encounters, setEncounters] = useState([]);
  const url = `${restBaseUrl}/location?v=default`;
  const { data: response, error, isLoading, mutate } = useSWR<{ data: any }, Error>(url, openmrsFetch);

  useEffect(() => {
    if (response) {
      response.data.results.sort(
        (a, b) => new Date(b.encounterDatetime).getTime() - new Date(a.encounterDatetime).getTime(),
      );

      setEncounters([...response.data.results]);
    }
  }, [response]);

  return {
    encounters,
    isLoading,
    isError: error,
  };
}
